import { equipmentPerformance, equipmentSeriesAndCapacity } from "./equipment-performance";
import reference from "../data/equipment-reference.json";

// Owner-provided September 15, 2026 Equipment sheet. This is a display-only
// reference, not catalog data: only existing, ID-and-name-matched rows appear.
// No names, prices, active states, or stored category assignments are replaced.
export const equipmentReferenceCount = reference.length;
const referenceById = new Map(reference.map((row) => [`st-price_book_item-${row.sourceId}`, row]));
const naturalOrder = new Intl.Collator("en", { numeric: true, sensitivity: "base" });
const normalized = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
const brandSpellings: Record<string, string> = {
  "ao smith": "AO Smith", "a. o. smith": "AO Smith", "a.o. smith": "AO Smith",
  "lg": "LG", "rgf": "RGF", "pmi": "PMI", "ibc": "IBC", "jci": "JCI",
};
const displayBrand = (value: string) => brandSpellings[normalized(value)] ??
  normalized(value).replace(/\b\w/g, (letter) => letter.toUpperCase());

export type EquipmentItem = {
  id: string; name: string; sku: string | null; categoryId: string;
  categoryIds: string[]; description: string | null;
  itemType?: "service" | "material" | "equipment" | null;
  equipmentDetails?: { manufacturer: string; type: string; model: string; efficiencyRatings: string; operatingStages: string } | null;
};
export type EquipmentEntry<T> = { item: T; brand: string; type: string; efficiency: string; stage: string; series: string; capacity: string; matched: boolean };

export function organizeEquipment<T extends EquipmentItem>(
  items: T[], categories: Array<{ id: string; kind: string }>,
): EquipmentEntry<T>[] {
  const equipmentCategories = new Set(categories.filter((category) => category.kind === "equipment").map((category) => category.id));
  return items.flatMap((item): EquipmentEntry<T>[] => {
    if (item.itemType && item.itemType !== "equipment") return [];
    const source = referenceById.get(item.id);
    const matched = source && normalized(source.name) === normalized(item.name) && item.sku === source.sourceId;
    if (!matched && item.itemType !== "equipment" && !item.equipmentDetails && !(item.categoryIds || [item.categoryId]).some((id) => equipmentCategories.has(id))) return [];
    const brandName = item.equipmentDetails?.manufacturer || (matched ? source.brand : "");
    const brand = brandName.trim() ? displayBrand(brandName) : "Brand to confirm";
    let type = item.equipmentDetails?.type.trim() || (matched ? source.type.trim() : "") || "Type to confirm";
    if (type === "Combi Boilder") type = "Combi boilers";
    if (brand === "Navien" && /^NPE\s*-?\s*\d/i.test(item.name)) type = "NPE tankless water heaters";
    const specifications = equipmentSeriesAndCapacity(`${item.name} ${item.description ?? ""}`);
    // Exact reference rows confirmed by the owner, September 18, 2026.
    if (matched && source.capacityTons) specifications.capacity = `${source.capacityTons} ton`;
    return [{ item, brand, type, ...equipmentPerformance(type, `${item.name} ${item.description ?? ""}`),
      ...(item.equipmentDetails?.efficiencyRatings ? { efficiency: item.equipmentDetails.efficiencyRatings } : {}),
      ...(item.equipmentDetails?.operatingStages ? { stage: item.equipmentDetails.operatingStages } : {}),
      ...specifications, matched: Boolean(matched) }];
  }).sort((a, b) => naturalOrder.compare(a.item.name, b.item.name) || naturalOrder.compare(a.item.id, b.item.id));
}

export function equipmentGroups<T>(entries: EquipmentEntry<T>[], field: "brand" | "type" | "efficiency" | "stage" | "series" | "capacity") {
  const groups = new Map<string, number>();
  for (const entry of entries) groups.set(entry[field], (groups.get(entry[field]) ?? 0) + 1);
  return [...groups].sort(([a], [b]) => {
    if (a.endsWith("to confirm") !== b.endsWith("to confirm")) return a.endsWith("to confirm") ? 1 : -1;
    return naturalOrder.compare(a, b);
  });
}
